import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * useReportData – fetches validated defects based on report filters and transforms into defectReport structure.
 *
 * @param {Object} filters
 *  - networkId
 *  - sectionsMode  ('all' | 'specific')
 *  - sectionIds []
 *  - startDate Date
 *  - endDate Date
 *  - selectAllDefects boolean
 *  - defectTypeIds []
 *  - selectAllVehicles boolean
 *  - deviceUuids []
 *  - includeRepeatedFindings boolean
 */
const useReportData = (filters, active) => {
  const [state, setState] = useState({ loading: false, error: null, inspection: null, defectReport: null, photos: [], severityLevels: [] });

  const fetchData = useCallback(async () => {
    if (!active) return;

    // Handle layout-only preview
    if (filters.isEmpty) {
      setState({
        loading: false,
        error: null,
        inspection: null,
        defectReport: { data: { total: 0, defects: [], defectTypes: [], photosWithDefects: [], photosWithDefectsCount: 0, repeatedFindingsMap: {} } },
        photos: [],
        severityLevels: []
      });
      return;
    }

    const { startDate, endDate } = filters;
    if (!startDate || !endDate) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const params = {
        validation_status: 'validated',
        from_date: startDate.toISOString().split('T')[0],
        to_date: endDate.toISOString().split('T')[0],
        limit: 10000,
        sort_by: 'first_detected_at',
        sort_order: 'ASC'
      };

      if (filters.selectAllVehicles === false && Array.isArray(filters.deviceUuids) && filters.deviceUuids.length > 0) {
        params.device_uuids = filters.deviceUuids.join(',');
      }

      const res = await api.get('/envis-findings/table-findings', { params });
      let findings = res.data?.data || [];

      // client-side filtering mirrors summary logic
      const { networkId, sectionsMode, sectionIds, selectAllDefects, defectTypeIds } = filters;
      if (networkId && networkId !== 'all') {
        findings = findings.filter(f => f.network_id?.toString() === networkId.toString());
      }
      if (sectionsMode === 'specific' && Array.isArray(sectionIds) && sectionIds.length > 0) {
        const sectionSet = new Set(sectionIds.map(id => id.toString()));
        findings = findings.filter(f => {
          const sectionIdVal = f.section_id || f.matched_section_id;
          return sectionIdVal && sectionSet.has(sectionIdVal.toString());
        });
      }
      if (!selectAllDefects && Array.isArray(defectTypeIds) && defectTypeIds.length > 0) {
        findings = findings.filter(f => {
          const dtId = f.validated_defect_type_id || f.defect_type_id;
          return defectTypeIds.includes(dtId);
        });
      }

      // Data now includes coordinates directly, so we just need to structure it
      let defectsWithBoundingBoxes = findings.map(f => ({
        ...f,
        bounding_box: (f.x1 !== null && f.y1 !== null && f.x2 !== null && f.y2 !== null)
          ? { x1: f.x1, y1: f.y1, x2: f.x2, y2: f.y2 }
          : null
      }));

      // Fetch repeated findings if enabled (all-time, no date restriction)
      let repeatedFindingsMap = {};
      if (filters.includeRepeatedFindings && defectsWithBoundingBoxes.length > 0) {
        const repeatedPromises = defectsWithBoundingBoxes.map(async (defect) => {
          try {
            const defectId = defect.defect_id || defect.id;
            const response = await api.get('/envis-findings/repeated-findings', {
              params: { confirmed_envis_defect_id: defectId, limit: 10000 }
            });
            const rawFindings = response.data?.data || [];
            if (rawFindings.length === 0) return;

            // Deduplicate by calendar day, pick largest bounding box per day
            const dayGroups = {};
            rawFindings.forEach(rf => {
              const dayKey = new Date(rf.detected_at).toISOString().split('T')[0];
              if (!dayGroups[dayKey]) dayGroups[dayKey] = [];
              dayGroups[dayKey].push(rf);
            });

            const uniqueFindings = Object.values(dayGroups).map(group => {
              return group.reduce((best, current) => {
                const bestArea = ((best.x2 || 0) - (best.x1 || 0)) * ((best.y2 || 0) - (best.y1 || 0));
                const currentArea = ((current.x2 || 0) - (current.x1 || 0)) * ((current.y2 || 0) - (current.y1 || 0));
                return currentArea > bestArea ? current : best;
              });
            });

            // Sort by detected_at ascending
            uniqueFindings.sort((a, b) => new Date(a.detected_at) - new Date(b.detected_at));

            if (uniqueFindings.length > 0) {
              repeatedFindingsMap[defectId] = uniqueFindings;
            }
          } catch (err) {
            console.error(`Failed to fetch repeated findings for defect ${defect.defect_id || defect.id}`, err);
          }
        });
        await Promise.all(repeatedPromises);
      }

      // Filter to only defects with repeated findings if requested
      if (filters.onlyDefectsWithRepeated) {
        defectsWithBoundingBoxes = defectsWithBoundingBoxes.filter(defect => {
          const defectId = defect.defect_id || defect.id;
          return (repeatedFindingsMap[defectId] || []).length > 0;
        });
      }

      // Compute summary stats from (possibly filtered) defects
      const total = defectsWithBoundingBoxes.length;
      const dtMap = {};
      defectsWithBoundingBoxes.forEach(f => {
        const dtId = f.validated_defect_type_id || f.defect_type_id;
        const dtName = f.validated_defect_type_name || f.defect_type_name;
        if (dtId) {
          if (!dtMap[dtId]) dtMap[dtId] = { defect_type_id: dtId, defect_type_name: dtName, count: 0 };
          dtMap[dtId].count += 1;
        }
      });

      // Build unique photo objects from the (possibly filtered) findings
      const photoMap = {};
      defectsWithBoundingBoxes.forEach(f => {
        if (f.original_image_path) {
          const raw = f.original_image_path.replace(/\\/g, '/');
          const path = raw.startsWith('/') ? raw.slice(1) : raw;
          if (/^(uploads|public\/uploads)/i.test(path)) {
            if (!photoMap[path]) {
              photoMap[path] = {
                id: f.photo_id,
                photo_path: path,
                url: path,
                has_annotation: true,
                annotation_count: 0,
                bounding_boxes: [],
                image_width: f.image_width,
                image_height: f.image_height,
                latitude: f.latitude || null,
                longitude: f.longitude || null,
                gimbal_yaw: f.gimbal_yaw || null,
                flight_yaw: f.flight_yaw || null
              };
            }
            photoMap[path].annotation_count += 1;
            if (!photoMap[path].latitude && f.latitude) {
              photoMap[path].latitude = f.latitude;
            }
            if (!photoMap[path].longitude && f.longitude) {
              photoMap[path].longitude = f.longitude;
            }
            if (!photoMap[path].gimbal_yaw && f.gimbal_yaw) {
              photoMap[path].gimbal_yaw = f.gimbal_yaw;
            }
            if (!photoMap[path].flight_yaw && f.flight_yaw) {
              photoMap[path].flight_yaw = f.flight_yaw;
            }
          }
        }
      });

      const photosArr = Object.values(photoMap);

      const defectReport = {
        data: {
          total,
          defects: defectsWithBoundingBoxes,
          defectTypes: Object.values(dtMap),
          photosWithDefects: photosArr,
          photosWithDefectsCount: photosArr.length,
          repeatedFindingsMap
        }
      };

      setState({ loading: false, error: null, inspection: null, defectReport, photos: photosArr, severityLevels: [] });
    } catch (err) {
      console.error('useReportData error', err);
      setState(prev => ({ ...prev, loading: false, error: 'Failed to load report data.' }));
    }
  }, [filters, active]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return state;
};

export default useReportData;
